import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getAppraisalProgressById, resetAppraisalProgressRecord } from "actions";
import { F } from "pages/AppraisalManagment/constants/legacyApiKeys";
import {
  buildSectionWeightageMap,
  mergeMyPafViewRecord,
  resolveEmployeePafSectionsData,
} from "pages/AppraisalManagment/utils/myPafHelpers";
import {
  compareSectionScoreGroupOrder,
  formatAppraisalScoreDisplay,
  formatSectionLabelWithWeight,
  isRenderableSectionScoreGroup,
  resolveSectionFirstColumnHeader,
  MONTHLY_APPRAISAL_RATING_SCALE,
  percentToPointRating,
  scoreFmt,
} from "pages/AppraisalManagment/utils/appraisalAverageRating";
import {
  formatMaxRatingLabel,
  scoreToPercentage,
} from "pages/AppraisalManagment/utils/appraisalProgressSummary";
import { sanitizeCkEditorHtml } from "configs";
import "./MonthlyAppraisalView.css";

/**
 * Strip report-only decoration (horizontal rules, underlines, bottom borders)
 * that CKEditor / Word paste often leave under description text.
 */
const stripDescriptionDecorations = (html) => {
  if (!html || typeof html !== "string") return "";
  if (typeof DOMParser === "undefined") {
    return html
      .replace(/<hr\b[^>]*\/?>/gi, "")
      .replace(/<\/?u\b[^>]*>/gi, "")
      .replace(/\s*text-decoration\s*:\s*underline\s*;?/gi, "")
      .replace(/\s*border-bottom(?:-(?:width|style|color))?\s*:[^;"']*;?/gi, "");
  }

  const doc = new DOMParser().parseFromString(
    `<div id="appraisal-desc-root">${html}</div>`,
    "text/html"
  );
  const root = doc.getElementById("appraisal-desc-root");
  if (!root) return html;

  root.querySelectorAll("hr, .ck-horizontal-line").forEach((el) => el.remove());

  root.querySelectorAll("u").forEach((el) => {
    const parent = el.parentNode;
    if (!parent) return;
    while (el.firstChild) {
      parent.insertBefore(el.firstChild, el);
    }
    parent.removeChild(el);
  });

  root.querySelectorAll("[style]").forEach((el) => {
    const style = el.getAttribute("style") || "";
    const cleaned = style
      .replace(/text-decoration\s*:\s*[^;]+;?/gi, "")
      .replace(/border-bottom(?:-(?:width|style|color))?\s*:[^;]+;?/gi, "")
      .replace(/;;+/g, ";")
      .trim()
      .replace(/^;|;$/g, "");
    if (cleaned) {
      el.setAttribute("style", cleaned);
    } else {
      el.removeAttribute("style");
    }
  });

  // Remove paragraphs that are only decorative lines (____ / ----).
  root.querySelectorAll("p, div").forEach((el) => {
    const text = (el.textContent || "").replace(/\u00a0/g, " ").trim();
    if (text.length >= 3 && /^[_\-\u2013\u2014=\s]+$/.test(text)) {
      el.remove();
    }
  });

  // Drop trailing empty blocks (often an underlined &nbsp; line under the text).
  const isEmptyBlock = (el) => {
    if (!el || !/^(P|DIV|SPAN)$/i.test(el.tagName)) return false;
    const text = (el.textContent || "").replace(/\u00a0/g, " ").trim();
    if (text !== "") return false;
    return !el.querySelector("img, table, iframe, video");
  };

  let child = root.lastElementChild;
  while (child && isEmptyBlock(child)) {
    const prev = child.previousElementSibling;
    child.remove();
    child = prev;
  }

  return root.innerHTML.trim();
};

/** Harden + strip Office/Excel paste CSS before dangerouslySetInnerHTML. */
const sanitizeAppraisalHtml = (html) => {
  if (!html || typeof html !== "string") return "";
  const hardened = sanitizeCkEditorHtml(html)
    .replace(/<script\b[\s\S]*?<\/script>/gi, "")
    .replace(/<iframe\b[\s\S]*?<\/iframe>/gi, "")
    .replace(/\s(on\w+)\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");

  return stripDescriptionDecorations(hardened);
};

const plainTextFromHtml = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value !== "string") {
    return String(value).trim();
  }
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const resolvePersonDisplayName = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object") {
    const nameParts = [value.firstname, value.middlename, value.lastname]
      .map((part) => (part == null ? "" : String(part).trim()))
      .filter(Boolean);
    if (nameParts.length > 0) return nameParts.join(" ");
    return String(
      value.full_name || value.name || value.employee_name || value.label || ""
    ).trim();
  }
  return "";
};

const resolveEvaluatedByName = (rec) => {
  const explicit =
    rec?.evaluated_by_name ||
    (typeof rec?.evaluated_by === "string" ? rec.evaluated_by : "") ||
    rec?.supervisor_name ||
    (typeof rec?.supervisor === "string" ? rec.supervisor : "");
  if (explicit) return String(explicit).trim();

  return (
    resolvePersonDisplayName(rec?.appraiser) ||
    resolvePersonDisplayName(rec?.employee?.supervisor) ||
    resolvePersonDisplayName(rec?.supervisor) ||
    ""
  );
};

const asDisplayDate = (raw) => {
  if (raw === null || raw === undefined || raw === "") return "";
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString("en-GB");
  }
  return String(raw);
};

const formatMaxRatingDisplay = (rating) => formatMaxRatingLabel(rating);

const OVERALL_RATING_DESCRIPTION_ROWS = [
  {
    ramp: "5.00",
    label: "Outstanding",
    description:
      "Unquestionably outstanding performance. Clearly exceeds performance requirements",
  },
  {
    ramp: "4.99 - 4.00",
    label: "Very Good",
    description:
      "Consistently meets performance requirements. Often surpasses normal expectations for important tasks",
  },
  {
    ramp: "3.99 - 3.00",
    label: "Good",
    description:
      "Overall performance is on target. Generally meets performance requirements",
  },
  {
    ramp: "2.99 - 2.00",
    label: "Needs Improvement",
    description:
      "Performance meets minimum acceptable standards, but certain areas of work require improvement",
  },
  {
    ramp: "1.99 - 1.00",
    label: "Unacceptable Performance",
    description:
      "Performance consistently falls short of desired results. Cannot be accepted on a continuing basis.",
  },
];

const MonthlyAppraisalView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const appraisalState = useSelector((state) => state.appraisalState);
  const printRef = useRef(null);
  const autoDownloadTriggeredRef = useRef(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const shouldAutoDownload = location.state?.autoDownload === true;
  const isMonthlyAppraisalReport = location.pathname.includes("monthlyappraisalreport");

  const queryParams = new URLSearchParams(location.search);
  const queryRecordId = queryParams.get("id");
  const recordId =
    location.state?.recordId ||
    queryRecordId ||
    appraisalState.appraisalProgressRecord?.id;

  const fallbackListRecord = useMemo(() => {
    const list = appraisalState.appraisalProgressList || [];
    if (!list.length) return null;
    if (queryRecordId) return list.find((item) => item?.id === queryRecordId) || null;
    return list[0];
  }, [appraisalState.appraisalProgressList, queryRecordId]);

  const rawRecord =
    location.state?.record ||
    appraisalState.appraisalProgressRecord ||
    fallbackListRecord ||
    {};

  const record = useMemo(() => {
    const list = appraisalState.appraisalProgressList || [];
    const matchedListRecord = list.find((item) =>
      item?.id === rawRecord?.id ||
      item?.id === recordId ||
      (rawRecord?.employee_id && item?.employee_id === rawRecord.employee_id)
    );

    const navigationRecord = location.state?.record || matchedListRecord || rawRecord || {};
    const apiRecord = {
      ...(matchedListRecord || {}),
      ...rawRecord,
      employee: rawRecord?.employee || matchedListRecord?.employee || null,
      appraiser: rawRecord?.appraiser || matchedListRecord?.appraiser || null,
      department: rawRecord?.department || matchedListRecord?.department || null,
      campaign: rawRecord?.campaign || matchedListRecord?.campaign || null,
      designation: rawRecord?.designation || matchedListRecord?.designation || null,
      employee_code: rawRecord?.employee_code || matchedListRecord?.employee_code || null,
      setup_name: rawRecord?.setup_name || matchedListRecord?.setup_name || null,
    };

    return mergeMyPafViewRecord(navigationRecord, apiRecord);
  }, [rawRecord, appraisalState.appraisalProgressList, recordId, location.state?.record]);

  useEffect(() => {
    if (recordId) {
      dispatch(getAppraisalProgressById(recordId, "detail"));
    }
  }, [recordId, dispatch]);

  const getValue = (value, fallback = "") => {
    if (value === null || value === undefined || value === "") return fallback;
    if (Array.isArray(value)) return value.length ? value.join(", ") : fallback;
    if (typeof value === "object") {
      return (
        value.name ||
        value.title ||
        value.label ||
        value.client_name ||
        value.designation_name ||
        value.employee_code ||
        fallback
      );
    }
    return String(value);
  };

  const parseCommentList = (value) => {
    if (!value) return [];

    const cleanCommentText = (text) =>
      String(text)
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/\s+/g, " ")
        .trim();

    if (Array.isArray(value)) {
      return value
        .map(cleanCommentText)
        .filter(Boolean);
    }
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed
            .map(cleanCommentText)
            .filter(Boolean);
        }
      } catch (_err) {
        // Keep string as single bullet.
      }
      return value
        .split(/\r?\n|•|,/)
        .map((item) => cleanCommentText(item))
        .filter(Boolean);
    }
    return [cleanCommentText(value)];
  };

  const normalizeSectionGroupLabel = (groupName = "") => {
    return String(groupName)
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  const resolveGroupWeightage = (groupValue) => {
    const explicitWeightage = Number(
      groupValue?.[F.groupWeightSynonym] ?? groupValue?.weightage
    );
    if (Number.isFinite(explicitWeightage) && explicitWeightage > 0) {
      return explicitWeightage;
    }

    const items = Array.isArray(groupValue)
      ? groupValue
      : Array.isArray(groupValue?.questions)
        ? groupValue.questions
        : [];

    const itemWeightages = items
      .map((item) => Number(item?.weightage))
      .filter((value) => Number.isFinite(value) && value > 0);

    if (!itemWeightages.length) return 0;

    const summedWeightage = itemWeightages.reduce((sum, value) => sum + value, 0);
    if (summedWeightage <= 100) return summedWeightage;

    const allEqual = itemWeightages.every((value) => value === itemWeightages[0]);
    return allEqual ? itemWeightages[0] : summedWeightage;
  };

  const sectionWeightageByTitle = useMemo(
    () => buildSectionWeightageMap(record),
    [record]
  );

  const sectionGroups = useMemo(() => {
    const sectionDataBlob = resolveEmployeePafSectionsData(record);
    if (!sectionDataBlob || typeof sectionDataBlob !== "object") return [];

    return Object.entries(sectionDataBlob)
      .filter(([groupName]) => isRenderableSectionScoreGroup(groupName))
      .map(([groupName, groupValue]) => {
      if (!isRenderableSectionScoreGroup(groupName)) return;
      const questionRows = Array.isArray(groupValue)
        ? groupValue
        : Array.isArray(groupValue?.questions)
          ? groupValue.questions
          : [];
      const rows = questionRows.map((item) => {
        const rawRating = item?.rating ?? item?.total_rating ?? null;
        const totalRating = Number(rawRating ?? 0);
        const obtainedRating = Number(item?.obtained_score ?? item?.obtained_rating ?? 0);
        const description =
          item?.description ??
          item?.[F.pivotRelation]?.description ??
          "";
        return {
          criteriaTitle: item?.title || item?.name || item?.[F.legacyNameSynonym] || groupName,
          descriptionHtml:
            description !== null &&
            description !== undefined &&
            String(description).trim() !== ""
              ? String(description)
              : "",
          comments:
            item?.comments !== null &&
            item?.comments !== undefined &&
            String(item.comments).trim() !== ""
              ? String(item.comments).trim()
              : "",
          raw_rating: rawRating,
          total_rating: Number.isNaN(totalRating) ? 0 : totalRating,
          obtained_rating: Number.isNaN(obtainedRating) ? 0 : obtainedRating,
          weightage: Number(item?.weightage ?? 0) || 0,
        };
      });

      const titleKey = String(groupName).trim().toLowerCase();
      const fromSectionWeights = sectionWeightageByTitle[titleKey];
      const sectionWeightage =
        Number.isFinite(fromSectionWeights) && fromSectionWeights > 0
          ? fromSectionWeights
          : resolveGroupWeightage(groupValue);

      return {
        key: groupName,
        label: normalizeSectionGroupLabel(groupName),
        weightage: sectionWeightage,
        rows,
      };
    })
      .filter(Boolean)
      .sort((a, b) => compareSectionScoreGroupOrder(a.key, b.key));
  }, [record, sectionWeightageByTitle]);

  const sectionScoring = useMemo(() => {
    const rowScore = (row) => {
      const maxRating = Number(row?.raw_rating ?? row?.total_rating) || 0;
      const obtained = Number(row?.obtained_rating) || 0;
      const percent = scoreToPercentage(obtained, maxRating);
      if (percent !== null) {
        return percentToPointRating(percent, MONTHLY_APPRAISAL_RATING_SCALE);
      }
      const total = Number(row?.total_rating) || 0;
      if (total > 0) {
        return Math.min(
          MONTHLY_APPRAISAL_RATING_SCALE,
          (obtained / total) * MONTHLY_APPRAISAL_RATING_SCALE
        );
      }
      return 0;
    };

    const sectionAvg = (group) => {
      if (!group?.rows?.length) {
        return 0;
      }
      const sum = group.rows.reduce((acc, r) => acc + rowScore(r), 0);
      return sum / group.rows.length;
    };

    const sectionWeighted = (group) => {
      const w = (Number(group?.weightage) || 0) / 100;
      return sectionAvg(group) * w;
    };

    const rowDisplayRating = (row) => {
      const obtained = Number(row?.obtained_rating);
      if (Number.isFinite(obtained)) {
        return obtained;
      }
      return rowScore(row);
    };

    const resultRowLabel = (group) =>
      formatSectionLabelWithWeight(group.label, group.weightage);

    const resultsRows = sectionGroups.map((g) => ({
      key: g.key,
      label: resultRowLabel(g),
      avgRating: sectionAvg(g),
      weighted: sectionWeighted(g),
    }));
    const totalAppraisal = resultsRows.reduce((sum, r) => sum + r.weighted, 0);

    const individualGroup = sectionGroups[0] || null;
    const behavioralGroup = sectionGroups.length > 1 ? sectionGroups[1] : null;

    const behavioralObtainedSum = behavioralGroup?.rows?.length
      ? behavioralGroup.rows.reduce(
        (s, r) => s + (Number(r.obtained_rating) || 0),
        0
      )
      : 0;
    const behavioralSectionAvg = behavioralGroup ? sectionAvg(behavioralGroup) : 0;
    const behavioralCount = behavioralGroup?.rows?.length || 0;

    return {
      rowScore,
      rowDisplayRating,
      sectionAvg,
      resultsRows,
      totalAppraisal,
      individualGroup,
      behavioralGroup,
      behavioralObtainedSum,
      behavioralSectionAvg,
      behavioralCount,
    };
  }, [sectionGroups]);

  const totalSectionWeightage = useMemo(() => {
    const apiTotal = Number(
      record?.total_section_weightage ?? record?.[F.totalWeightSynonym]
    );
    if (Number.isFinite(apiTotal) && apiTotal > 0) {
      return apiTotal;
    }
    return sectionGroups.reduce((sum, group) => sum + (Number(group.weightage) || 0), 0);
  }, [record?.total_section_weightage, record?.[F.totalWeightSynonym], sectionGroups]);

  const weightageWarning =
    totalSectionWeightage > 100 ? "Total weightage cannot exceed 100%" : "";

  const totalWeightageText = `Total Weightage: ${totalSectionWeightage}%`;

  const overallSectionTotals = useMemo(() => {
    return sectionGroups.reduce(
      (acc, row) => ({
        total: acc.total + Number(row.rows?.reduce((sum, item) => sum + Number(item.total_rating || 0), 0) || 0),
        obtained: acc.obtained + Number(row.rows?.reduce((sum, item) => sum + Number(item.obtained_rating || 0), 0) || 0),
      }),
      { total: 0, obtained: 0 }
    );
  }, [sectionGroups]);

  const appraisalDate = record?.appraisal_date_time || record?.created_at;
  const monthFromRecord = record?.month
    ? `${String(record.month).charAt(0).toUpperCase()}${String(record.month).slice(1).toLowerCase()}`
    : null;
  const yearFromDate =
    appraisalDate && !Number.isNaN(new Date(appraisalDate).getTime())
      ? new Date(appraisalDate).getFullYear()
      : null;
  const monthYear =
    monthFromRecord && yearFromDate
      ? `${monthFromRecord}, ${yearFromDate}`
      : appraisalDate && !Number.isNaN(new Date(appraisalDate).getTime())
        ? new Date(appraisalDate).toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
          })
        : "January, 2026";

  const overallPercentage = getValue(
    record?.overall_score ??
      record?.sections_data?.overall_score ??
      record?.[F.sectionDataSynonym]?.overall_score ??
      (overallSectionTotals.total ? Math.round((overallSectionTotals.obtained / overallSectionTotals.total) * 100) : null)
  );

  const appraiserComments = parseCommentList(record?.appraisers_comments);
  const developmentComments = parseCommentList(record?.developmental_plan);
  const employeeInfo = record?.employee_info || record?.employee || {};
  const employeeCode = getValue(
    record?.employee_code || employeeInfo?.employee_code,
    "-"
  );
  const appraisalTitle = getValue(
    record?.setup_name ||
      employeeInfo?.appraisal?.[0]?.title ||
      employeeInfo?.appraisal_search_text,
    "-"
  );
  const departmentName = getValue(
    record?.department_name ||
      record?.department?.name ||
      record?.department ||
      employeeInfo?.department?.name ||
      employeeInfo?.department,
    "-"
  );
  const designationName = getValue(
    record?.designation_name ||
    record?.designation?.name ||
      record?.designation?.designation_name ||
      record?.designation ||
    employeeInfo?.designation?.name ||
    employeeInfo?.designation?.designation_name ||
    employeeInfo?.designation,
    "-"
  );

  const signatureImage = record?.signature_image || null;
  const employeeSignature = record?.employee_signature || null;
  const employeeSignedDateRaw = record?.employee_signed_at || null;
  const employeeSignedDate =
    employeeSignedDateRaw && !Number.isNaN(new Date(employeeSignedDateRaw).getTime())
      ? new Date(employeeSignedDateRaw).toLocaleDateString("en-GB")
      : "";
  const signOffDateRaw =
    record?.sign_off_date_time || record?.updated_at || record?.created_at;
  const signOffDate =
    signOffDateRaw && !Number.isNaN(new Date(signOffDateRaw).getTime())
      ? new Date(signOffDateRaw).toLocaleDateString("en-GB")
      : "";

  const navy = "#1A1B2F";
  const fieldBox = {
    background: "#F3F4F6",
    border: "1px solid #E5E7EB",
    borderRadius: "8px",
    padding: "10px 12px",
    color: "#4B5563",
    fontSize: "14px",
    minHeight: "40px",
    display: "flex",
    alignItems: "center",
  };
  const sectionBar = {
    background: navy,
    color: "#fff",
    padding: "10px 16px",
    fontWeight: 600,
    fontSize: "13px",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  };

  const employeeName = getValue(
    record?.employee_name || employeeInfo?.name || record?.name,
    "-"
  );
  const dateHired = getValue(
    record?.date_hired_formatted ||
      employeeInfo?.start_date_formatted ||
      asDisplayDate(record?.date_hired || employeeInfo?.start_date),
    "-"
  );
  const appraisalPeriodLabel = (() => {
    if (monthFromRecord && yearFromDate) {
      return `${monthFromRecord} ${yearFromDate}`;
    }
    if (record?.appraisal_period) {
      return getValue(record.appraisal_period, "-");
    }
    if (record?.appraisal_types) {
      return getValue(record.appraisal_types, "-");
    }
    return "-";
  })();
  const evaluatedBy = resolveEvaluatedByName(record) || "-";
  const lastReview = getValue(
    asDisplayDate(
      record?.date_last_modified ||
        record?.date_of_last_review ||
        record?.last_review_date ||
        record?.last_review
    ),
    "-"
  );
  const departmentSectionTeam = departmentName;
  const evaluationDateStr = getValue(
    record?.evaluation_date_formatted ||
      record?.appraisal_date_time_formatted ||
      asDisplayDate(record?.evaluation_date || appraisalDate),
    "-"
  );
  const dateAccomplished = getValue(
    record?.date_accomplished_formatted ||
      asDisplayDate(record?.date_accomplished || record?.sign_off_date_time),
    "-"
  );
  const reviewedVerifiedBy = getValue(
    record?.reviewed_and_verified_by_name || record?.reviewed_by || record?.verified_by,
    "-"
  );

  const monthKey = String(record?.month || "")
    .trim()
    .toLowerCase();
  const monthToQuarter = {
    january: 1,
    february: 1,
    march: 1,
    april: 2,
    may: 2,
    june: 2,
    july: 3,
    august: 3,
    september: 3,
    october: 4,
    november: 4,
    december: 4,
  };
  const quarterNum = monthKey ? monthToQuarter[monthKey] : null;
  const quarterReviewLabel =
    quarterNum && yearFromDate
      ? `Quarter ${quarterNum} ${yearFromDate} Review`
      : "Quarterly Review";

  const purposeKeyFromRecord = String(
    record?.purpose_of_review || record?.review_purpose || ""
  )
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  const purposeSelected =
    {
      third_month: "third_month",
      "3rd_month": "third_month",
      "3rd_month_review": "third_month",
      regularization: "regularization",
      job_promotion: "job_promotion",
      promotion: "job_promotion",
      monthly: "monthly",
      monthly_review: "monthly",
      lateral: "lateral",
      lateral_transfer: "lateral",
      quarter: "quarter",
    }[purposeKeyFromRecord] || "monthly";

  const purposeOptions = [
    { id: "third_month", label: "3rd Month review" },
    { id: "regularization", label: "Regularization" },
    { id: "job_promotion", label: "Job Promotion" },
    { id: "monthly", label: "Monthly Review" },
    { id: "lateral", label: "Lateral Transfer" },
    { id: "quarter", label: quarterReviewLabel },
  ];

  const nextLevelDate = asDisplayDate(
    record?.next_level_date || record?.next_level_review_date
  );
  const nextLevelSignature = record?.next_level_signature;
  const nextLevelIsImage =
    typeof nextLevelSignature === "string" &&
    /^https?:\/\//i.test(nextLevelSignature.trim());

  const employeeCommentsText = (() => {
    const raw =
      record?.employee_comments ||
      record?.employee_comment ||
      record?.employee_feedback ||
      record?.employee_s_comments;
    if (raw === null || raw === undefined || raw === "") {
      return "";
    }
    return plainTextFromHtml(String(raw));
  })();

  const behavioralCompetencies = (() => {
    return plainTextFromHtml(
      String(record?.behavioral_dimensions_competencies ?? record?.behavioral_dimensions ?? "")
    );
  })();

  const fieldLabel = {
    fontSize: "12px",
    color: "#111827",
    marginBottom: "6px",
    fontWeight: 600,
  };

  const ratingCellFmt = (n) => formatAppraisalScoreDisplay(n, "");

  const overallEvaluatorDateForComments = asDisplayDate(
    record?.evaluator_comment_date || record?.evaluator_date || signOffDateRaw
  );

  const listPath = (() => {
    const from = location.state?.from;
    const viewRoutes = ["/monthlyappraisalview", "/monthlyappraisalreport"];
    if (from && !viewRoutes.some((route) => String(from).startsWith(route))) {
      return from;
    }
    return isMonthlyAppraisalReport ? "/monthly-reports" : "/appraisal";
  })();

  const handleCancel = () => {
    dispatch(resetAppraisalProgressRecord());
    navigate(listPath, { replace: true, state: null });
  };

  const handleDownloadPdf = async () => {
    const contentEl = printRef.current;
    if (!contentEl) {
      return;
    }
    if (isDownloadingPdf) return;

    try {
      setIsDownloadingPdf(true);
      const html2pdfModule = await import("html2pdf.js");
      const html2pdf = html2pdfModule.default || html2pdfModule;
      const filenameSafeMonth = String(monthYear || "monthly-appraisal")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

      await html2pdf()
        .set({
          margin: [8, 8, 8, 8],
          filename: `monthly-appraisal-${filenameSafeMonth}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            scrollY: 0,
          },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"] },
        })
        .from(contentEl)
        .save();
    } catch (error) {
      // Keep a non-blocking fallback for unexpected PDF generation issues.
      console.error("Error downloading appraisal PDF:", error);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  useEffect(() => {
    if (!shouldAutoDownload || autoDownloadTriggeredRef.current || !recordId) {
      return;
    }

    const detailLoaded =
      appraisalState.appraisalProgressRecord?.id === recordId ||
      location.state?.record?.id === recordId;

    if (!detailLoaded || !printRef.current) {
      return;
    }

    const timer = window.setTimeout(() => {
      if (autoDownloadTriggeredRef.current || !printRef.current) {
        return;
      }
      autoDownloadTriggeredRef.current = true;
      handleDownloadPdf();
    }, 800);

    return () => window.clearTimeout(timer);
  }, [
    shouldAutoDownload,
    recordId,
    appraisalState.appraisalProgressRecord,
    location.state?.record?.id,
  ]);

  const renderScoringSectionTable = (group) => {
    if (!group) return null;

    const tableColumnCount = 4;
    const commentsFieldLabel = "Comments";

    const sectionTotalRating = (group.rows || []).reduce(
      (sum, row) => sum + (Number(row.total_rating) || 0),
      0
    );
    const sectionObtainRating = (group.rows || []).reduce(
      (sum, row) => sum + (Number(row.obtained_rating) || 0),
      0
    );
    const sectionObtainPercent =
      sectionTotalRating > 0 ? (sectionObtainRating / sectionTotalRating) * 100 : 0;

    const sectionTitle = formatSectionLabelWithWeight(group.label, group.weightage);
    const firstColumnHeader = resolveSectionFirstColumnHeader(group.label || group.key);

    return (
      <div
        key={group.key}
        style={{
          marginBottom: "20px",
          border: "1px solid #E5E7EB",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <div style={{ ...sectionBar, textAlign: "center" }}>{sectionTitle}</div>
        <div className="table-responsive">
          <table className="table mb-0" style={{ borderColor: "#E5E7EB" }}>
            <thead>
              <tr style={{ background: "#F0F2F5" }}>
                <th
                  style={{
                    borderColor: "#E5E7EB",
                    fontWeight: 600,
                    padding: "10px 12px",
                    textAlign: "left",
                    width: "18%",
                  }}
                >
                  {firstColumnHeader}
                </th>
                <th
                  style={{
                    borderColor: "#E5E7EB",
                    fontWeight: 600,
                    padding: "10px 12px",
                    textAlign: "left",
                    width: "44%",
                  }}
                >
                  Description
                </th>
                <th
                  style={{
                    borderColor: "#E5E7EB",
                    fontWeight: 600,
                    padding: "10px 12px",
                    textAlign: "right",
                    width: "19%",
                  }}
                >
                  Rating
                </th>
                <th
                  style={{
                    borderColor: "#E5E7EB",
                    fontWeight: 600,
                    padding: "10px 12px",
                    textAlign: "right",
                    width: "19%",
                  }}
                >
                  Obtained Rating
                </th>
              </tr>
            </thead>
            <tbody>
              {group.rows?.length ? (
                group.rows.map((row, index) => (
                  <tr key={`${group.key}-${index}`}>
                    <td
                      style={{
                        borderColor: "#E5E7EB",
                        padding: "10px 12px",
                        verticalAlign: "top",
                        fontWeight: 600,
                      }}
                    >
                      {row.criteriaTitle || "—"}
                    </td>
                    <td
                      style={{
                        borderColor: "#E5E7EB",
                        padding: "10px 12px",
                        verticalAlign: "top",
                        color: "#4B5563",
                        fontSize: "13px",
                      }}
                    >
                      {row.descriptionHtml ? (
                        <div
                          className="monthly-appraisal-description"
                          style={{ lineHeight: 1.5 }}
                          dangerouslySetInnerHTML={{
                            __html: sanitizeAppraisalHtml(row.descriptionHtml),
                          }}
                        />
                      ) : null}
                      {row.comments ? (
                        <div
                          style={{
                            marginTop: row.descriptionHtml ? "10px" : 0,
                            fontSize: "12px",
                            lineHeight: 1.5,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          <span style={{ fontWeight: 600, color: "#111827" }}>
                            {commentsFieldLabel}:{" "}
                          </span>
                          {row.comments}
                        </div>
                      ) : null}
                    </td>
                    <td
                      style={{
                        borderColor: "#E5E7EB",
                        padding: "10px 12px",
                        textAlign: "right",
                        verticalAlign: "top",
                      }}
                    >
                      {formatMaxRatingDisplay(row.raw_rating ?? row.total_rating)}
                    </td>
                    <td
                      style={{
                        borderColor: "#E5E7EB",
                        padding: "10px 12px",
                        textAlign: "right",
                        verticalAlign: "top",
                        fontWeight: 600,
                      }}
                    >
                      {ratingCellFmt(row.obtained_rating) || "—"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={tableColumnCount} style={{ borderColor: "#E5E7EB" }} />
                </tr>
              )}
              <tr style={{ background: "#DBEAFE", fontWeight: 700, color: "#1E3A8A" }}>
                <td
                  colSpan={tableColumnCount - 1}
                  style={{ borderColor: "#E5E7EB", padding: "10px 12px", fontWeight: 700 }}
                >
                  Section Total
                </td>
                <td
                  style={{
                    borderColor: "#E5E7EB",
                    padding: "10px 12px",
                    textAlign: "right",
                    fontWeight: 700,
                  }}
                >
                  {`${scoreFmt(sectionObtainPercent)}%`}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div style={{ background: "#f5f6f8", minHeight: "100vh", padding: "20px" }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <h2 style={{ fontSize: "20px", fontWeight: 600, marginBottom: "16px" }}>
          {location.pathname.includes("/quarterly-appraisal/paf/view")
            ? `Quarterly Appraisal View (${monthYear})`
            : location.pathname.includes("/yearly-appraisals/paf/view")
              ? `Yearly Appraisal View (${monthYear})`
              : `Monthly Appraisal ${isMonthlyAppraisalReport ? "Report" : "View"} (${monthYear})`}
        </h2>

        <div
          ref={printRef}
          style={{
            fontFamily: '"Segoe UI", "Open Sans", system-ui, sans-serif',
            color: "#111827",
            background: "#fff",
            padding: "20px 16px 28px",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* — PDF: Performance Appraisal form header (sample layout) — */}
          <div
            className="mb-4"
            style={{ breakInside: "avoid", textAlign: "center", pageBreakInside: "avoid" }}
          >
            <div style={{ marginBottom: "12px" }}>
              <img
                src="/assets/images/NX-logo.svg"
                alt="Premier nx"
                style={{ height: "32px", width: "auto" }}
              />
            </div>
            <h1
              style={{
                margin: 0,
                fontSize: "16px",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "#111827",
              }}
            >
              Performance appraisal form - staff
            </h1>
            <p
              style={{
                margin: "6px 0 0",
                fontSize: "12px",
                color: "#6B7280",
                fontStyle: "italic",
              }}
            >
              (Strictly Confidential)
            </p>
          </div>

          <div
            className="mb-3"
            style={{
              background: "#fff",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              border: "1px solid #E5E7EB",
              overflow: "hidden",
              breakInside: "avoid",
              pageBreakInside: "avoid",
            }}
          >
            <div style={{ ...sectionBar, textAlign: "center" }}>Employee Information</div>
            <div
              style={{
                padding: "18px 16px 20px",
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: "16px 24px",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#111827",
                    marginBottom: "6px",
                    fontWeight: 600,
                  }}
                >
                  Employee Name
                </div>
                <div style={fieldBox}>{employeeName}</div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#111827",
                    marginBottom: "6px",
                    fontWeight: 600,
                  }}
                >
                  Department / Section / Team
                </div>
                <div style={fieldBox}>{departmentSectionTeam}</div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#111827",
                    marginBottom: "6px",
                    fontWeight: 600,
                  }}
                >
                  Date Hired
                </div>
                <div style={fieldBox}>{dateHired}</div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#111827",
                    marginBottom: "6px",
                    fontWeight: 600,
                  }}
                >
                  Position / Level
                </div>
                <div style={fieldBox}>{designationName}</div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#111827",
                    marginBottom: "6px",
                    fontWeight: 600,
                  }}
                >
                  Appraisal Period
                </div>
                <div style={fieldBox}>{appraisalPeriodLabel}</div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#111827",
                    marginBottom: "6px",
                    fontWeight: 600,
                  }}
                >
                  Date Accomplished
                </div>
                <div style={fieldBox}>{dateAccomplished}</div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#111827",
                    marginBottom: "6px",
                    fontWeight: 600,
                  }}
                >
                  Evaluated By
                </div>
                <div style={fieldBox}>{evaluatedBy}</div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#111827",
                    marginBottom: "6px",
                    fontWeight: 600,
                  }}
                >
                  Evaluation Date
                </div>
                <div style={fieldBox}>{evaluationDateStr}</div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#111827",
                    marginBottom: "6px",
                    fontWeight: 600,
                  }}
                >
                  Date of Last Review
                </div>
                <div style={fieldBox}>{lastReview}</div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#111827",
                    marginBottom: "6px",
                    fontWeight: 600,
                  }}
                >
                  Reviewed and Verified By
                </div>
                <div style={fieldBox}>{reviewedVerifiedBy}</div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#111827",
                    marginBottom: "6px",
                    fontWeight: 600,
                  }}
                >
                  Employee Code
                </div>
                <div style={fieldBox}>{employeeCode}</div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#111827",
                    marginBottom: "6px",
                    fontWeight: 600,
                  }}
                >
                  Appraisal Title
                </div>
                <div style={fieldBox}>{appraisalTitle}</div>
              </div>
            </div>
          </div>

          {/* — Purpose of review — */}
          <div
            className="mb-3"
            style={{
              background: "#fff",
              borderRadius: "8px",
              border: "1px solid #E5E7EB",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              overflow: "hidden",
              breakInside: "avoid",
              pageBreakInside: "avoid",
            }}
          >
            <div style={{ padding: "16px 16px 8px" }}>
              <p style={{ margin: 0, fontSize: "14px", fontWeight: 700, color: "#111827" }}>
                Purpose of review:
              </p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: "10px 20px",
                  marginTop: "12px",
                }}
              >
                {purposeOptions.map((opt) => {
                  const selected = purposeSelected === opt.id;
                  return (
                    <div
                      key={opt.id}
                      style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "13px" }}
                    >
                      <span
                        style={{
                          width: "16px",
                          height: "16px",
                          borderRadius: "50%",
                          border: `2px solid ${navy}`,
                          flexShrink: 0,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        aria-hidden
                      >
                        {selected ? (
                          <span
                            style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              background: navy,
                            }}
                          />
                        ) : null}
                      </span>
                      <span style={{ color: selected ? "#111827" : "#4B5563" }}>{opt.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div
            className="mb-3 monthly-appraisal-scoring"
            style={{
              breakBefore: "page",
              pageBreakBefore: "always",
              background: "#fff",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              border: "1px solid #E5E7EB",
            }}
          >
            <div
              style={{
                background: "#EEF1F4",
                padding: "10px 16px",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "12px",
                borderBottom: "1px solid #E5E7EB",
              }}
            >
              <div style={{ textAlign: "right" }}>
                <span
                  style={{
                    fontSize: "12px",
                    lineHeight: 1.2,
                    background: "#DBEAFE",
                    color: "#1E3A5F",
                    borderRadius: "999px",
                    padding: "4px 12px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    display: "inline-block",
                  }}
                >
                  {totalWeightageText}
                </span>
                {weightageWarning ? (
                  <div style={{ color: "#dc2626", fontSize: "12px", marginTop: "6px", fontWeight: 600 }}>
                    {weightageWarning}
                  </div>
                ) : null}
              </div>
            </div>

            <div style={{ padding: "8px 16px 20px" }}>
              {!sectionGroups.length ? null : (
                <>
                  {sectionGroups.map((group) => renderScoringSectionTable(group))}

                  <div
                    style={{
                      marginBottom: "20px",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      overflow: "hidden",
                    }}
                  >
                    <div style={{ ...sectionBar, textAlign: "center" }}>
                      {`Overall Results Points${
                        String(overallPercentage || "").trim()
                          ? ` - Overall score: ${overallPercentage}%`
                          : ""
                      }`}
                    </div>
                    <div className="table-responsive">
                      <table className="table mb-0" style={{ borderColor: "#E5E7EB" }}>
                        <thead>
                          <tr style={{ background: "#F0F2F5" }}>
                            <th
                              style={{
                                borderColor: "#E5E7EB",
                                fontWeight: 600,
                                padding: "10px 12px",
                                textAlign: "left",
                              }}
                            >
                              Section
                            </th>
                            <th
                              style={{
                                borderColor: "#E5E7EB",
                                fontWeight: 600,
                                padding: "10px 12px",
                                textAlign: "right",
                              }}
                            >
                              Average Rating
                            </th>
                            <th
                              style={{
                                borderColor: "#E5E7EB",
                                fontWeight: 600,
                                padding: "10px 12px",
                                textAlign: "right",
                              }}
                            >
                              Weighted Points
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sectionScoring.resultsRows.map((r) => (
                            <tr key={r.key}>
                              <td style={{ borderColor: "#E5E7EB", padding: "10px 12px" }}>{r.label}</td>
                              <td
                                style={{
                                  borderColor: "#E5E7EB",
                                  padding: "10px 12px",
                                  textAlign: "right",
                                }}
                              >
                                {scoreFmt(r.avgRating)}
                              </td>
                              <td
                                style={{
                                  borderColor: "#E5E7EB",
                                  padding: "10px 12px",
                                  textAlign: "right",
                                }}
                              >
                                {scoreFmt(r.weighted)}
                              </td>
                            </tr>
                          ))}
                          <tr style={{ background: "#F0F2F5", fontWeight: 700 }}>
                            <td style={{ borderColor: "#E5E7EB", padding: "10px 12px" }}>Total Appraisal Rating</td>
                            <td style={{ borderColor: "#E5E7EB" }} />
                            <td
                              style={{
                                borderColor: "#E5E7EB",
                                padding: "10px 12px",
                                textAlign: "right",
                              }}
                            >
                              {scoreFmt(sectionScoring.totalAppraisal)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div
                    className="mb-0"
                    style={{
                      marginTop: "20px",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      overflow: "hidden",
                      breakInside: "avoid",
                      pageBreakInside: "avoid",
                    }}
                  >
                    <div style={{ ...sectionBar, textAlign: "center" }}>Overall Rating Description</div>
                    <div style={{ padding: "16px" }}>
                      <p
                        style={{
                          margin: "0 0 12px",
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#111827",
                          textAlign: "center",
                        }}
                      >
                        (Rate the employee&apos;s performance on a scale of 1 to 5 defined as follows)
                      </p>
                      <div className="table-responsive">
                        <table className="table mb-0" style={{ borderColor: "#E5E7EB" }}>
                          <thead>
                            <tr style={{ background: "#F0F2F5" }}>
                              <th
                                style={{
                                  borderColor: "#E5E7EB",
                                  fontWeight: 600,
                                  padding: "10px 12px",
                                  width: "18%",
                                  textAlign: "center",
                                }}
                              >
                                RAMP
                              </th>
                              <th
                                style={{
                                  borderColor: "#E5E7EB",
                                  fontWeight: 600,
                                  padding: "10px 12px",
                                  width: "22%",
                                }}
                              >
                                &nbsp;
                              </th>
                              <th
                                style={{
                                  borderColor: "#E5E7EB",
                                  fontWeight: 600,
                                  padding: "10px 12px",
                                }}
                              >
                                DESCRIPTION
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {OVERALL_RATING_DESCRIPTION_ROWS.map((row) => (
                              <tr key={row.ramp}>
                                <td
                                  style={{
                                    borderColor: "#E5E7EB",
                                    padding: "10px 12px",
                                    fontWeight: 600,
                                    textAlign: "center",
                                    verticalAlign: "top",
                                  }}
                                >
                                  {row.ramp}
                                </td>
                                <td
                                  style={{
                                    borderColor: "#E5E7EB",
                                    padding: "10px 12px",
                                    fontWeight: 600,
                                    verticalAlign: "top",
                                  }}
                                >
                                  {row.label}
                                </td>
                                <td
                                  style={{
                                    borderColor: "#E5E7EB",
                                    padding: "10px 12px",
                                    verticalAlign: "top",
                                  }}
                                >
                                  {row.description}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p
                        style={{
                          margin: "12px 0 0",
                          fontSize: "12px",
                          color: "#374151",
                          lineHeight: 1.5,
                        }}
                      >
                        <strong>Note:</strong> If an immediate superior is found to have rated too high, too
                        low or too similar, the next management level will make necessary adjustments.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Overall comments (sample p.3) ── */}
          <div
            className="mb-3"
            style={{
              breakBefore: "page",
              pageBreakBefore: "always",
              background: "#fff",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              border: "1px solid #E5E7EB",
              overflow: "hidden",
            }}
          >
            <div style={{ ...sectionBar, textAlign: "center" }}>Overall Comments</div>
            <div style={{ padding: "16px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: employeeSignature
                    ? "repeat(2, minmax(0, 1fr))"
                    : "minmax(0, 1fr)",
                  gap: "16px 24px",
                  maxWidth: employeeSignature ? "100%" : "50%",
                }}
              >
                <div>
                  <div style={fieldLabel}>Evaluator's Signature</div>
                  <div style={fieldBox}>
                    {signatureImage ? (
                      <img
                        src={signatureImage}
                        alt="Evaluator's Signature"
                        style={{ maxHeight: "48px", objectFit: "contain" }}
                      />
                    ) : null}
                  </div>
                  {overallEvaluatorDateForComments ? (
                    <div style={{ ...fieldLabel, marginTop: "8px", marginBottom: 0 }}>
                      Signed on {overallEvaluatorDateForComments}
                    </div>
                  ) : null}
                </div>
                {employeeSignature ? (
                  <div>
                    <div style={fieldLabel}>Employee Signature</div>
                    <div style={fieldBox}>
                      <img
                        src={employeeSignature}
                        alt="Employee Signature"
                        style={{ maxHeight: "48px", objectFit: "contain" }}
                      />
                    </div>
                    {employeeSignedDate ? (
                      <div style={{ ...fieldLabel, marginTop: "8px", marginBottom: 0 }}>
                        Signed on {employeeSignedDate}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* ── Appraiser's Comments ── */}
          <div
            className="mb-3"
            style={{
              background: "#fff",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              border: "1px solid #E5E7EB",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                ...sectionBar,
                textTransform: "none",
                letterSpacing: "normal",
              }}
            >
              Appraiser&apos;s Comments
            </div>
            <div style={{ padding: "16px" }}>
              {appraiserComments.map((comment, idx) => (
                <div key={`appraiser-${idx}`}>{comment}</div>
              ))}
            </div>
          </div>

          {/* ── Developmental Plan ── */}
          <div
            className="mb-3"
            style={{
              background: "#fff",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              border: "1px solid #E5E7EB",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                ...sectionBar,
                textTransform: "none",
                letterSpacing: "normal",
              }}
            >
              Developmental Plan
            </div>
            <div style={{ padding: "16px" }}>
              {developmentComments.map((comment, idx) => (
                <div key={`dev-${idx}`}>{comment}</div>
              ))}
            </div>
          </div>

        </div>

        {/* ── Action Buttons ── */}
        <div className="d-flex align-items-center gap-2 mt-2">
          <button type="button" className="c-b-btn" onClick={handleCancel}>
            Cancel
          </button>
          <button type="button" className="n-u-btn" onClick={handleDownloadPdf} disabled={isDownloadingPdf}>
            <img src="/assets/images/download-pdf-icon.svg" alt="download" className="me-2" />
            {isDownloadingPdf ? "Downloading..." : "Download as PDF"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MonthlyAppraisalView;